import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DataTable } from '@/components/CustomTable'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import AppDialog, { ModalActions } from '@/components/CustomModal'
import Loading from '@/components/Loading'
import { Pencil, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import type { ColumnDef } from '@tanstack/react-table'

import {
  useDepotsQuery,
  useCreateDepotMutation,
  useUpdateDepotMutation,
  useDeleteDepotMutation,
} from '@/hooks/useDepots'
import type { Depot } from '@/types/backend'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export default function DepotsPage() {
  const navigate = useNavigate()

  const [search, _setSearch] = useState('')

  // /depots ne pagine pas → on récupère tout et on filtre localement
  const { data: depots = [], status, isFetching } = useDepotsQuery(true)

  const filtered: Depot[] = useMemo(() => {
    const q = search.trim().toLowerCase()
    return depots.filter((d) => {
      const okSearch =
        !q ||
        d.name?.toLowerCase().includes(q) ||
        d.address?.toLowerCase().includes(q) ||
        d.code?.toLowerCase().includes(q)
      return okSearch
    })
  }, [depots, search])

  // modales/états
  const [open, setOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  const [editing, setEditing] = useState<Depot | null>(null)
  const [form, setForm] = useState<{
    name: string
    address: string
    isDefault: 'YES' | 'NO'
  }>({
    name: '',
    address: '',
    isDefault: 'NO',
  })

  // mutations
  const createMut = useCreateDepotMutation()
  const updateMut = useUpdateDepotMutation(editing?.id || '')
  const deleteMut = useDeleteDepotMutation()

  const columns = useMemo<ColumnDef<Depot>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Nom',
        cell: ({ row }) => (
          <button
            onClick={() => navigate(`/depots/${row.original.id}`)}
            className='font-medium underline-offset-2 hover:underline'
          >
            {row.original.name}
          </button>
        ),
      },
      {
        accessorKey: 'address',
        header: 'Adresse',
        cell: ({ row }) => row.original.address ?? '—',
      },
      {
        accessorKey: 'isDefault',
        header: 'Défaut',
        cell: ({ row }) => (row.original.isDefault ? '★ Oui' : '—'),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className='flex items-center gap-3'>
            <Pencil
              size={18}
              className='cursor-pointer text-green-600 mr-4'
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                setEditing(row.original)
                setForm({
                  name: row.original.name ?? '',
                  address: row.original.address ?? '',
                  isDefault: row.original.isDefault ? 'YES' : 'NO',
                })
                setOpen(true)
              }}
            />
            <Trash2
              size={18}
              className='cursor-pointer text-red-600 ml-4'
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                setPendingDeleteId(row.original.id)
                setConfirmOpen(true)
              }}
            />
          </div>
        ),
      },
    ],
    [navigate]
  )

  const openCreate = () => {
    setEditing(null)
    setForm({ name: '', address: '', isDefault: 'NO' })
    setOpen(true)
  }

  const submit = async () => {
    const name = form.name.trim()
    const address = form.address.trim() || undefined
    const isDefault = form.isDefault === 'YES'
    if (!name) return toast.error('Nom requis')

    try {
      if (editing) {
        await updateMut.mutateAsync({ name, address, isDefault })
        toast.success('Dépôt modifié')
      } else {
        await createMut.mutateAsync({ name, address, isDefault })
        toast.success('Dépôt créé')
      }
      setOpen(false)
    } catch (e: any) {
      toast.error('Erreur', {
        description: e?.response?.data?.message ?? 'Action échouée',
      })
    }
  }

  const confirmDelete = async () => {
    if (!pendingDeleteId) return
    try {
      await deleteMut.mutateAsync(pendingDeleteId)
      toast.success('Dépôt supprimé')
      setConfirmOpen(false)
      setPendingDeleteId(null)
    } catch (e: any) {
      toast.error('Suppression échouée', {
        description: e?.response?.data?.message,
      })
    }
  }

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between gap-3'>
        <h3 className='text-2xl font-semibold'>Dépôts</h3>
        <div className='flex items-center gap-2'>
          <Button onClick={openCreate}>Ajouter</Button>
        </div>
      </div>

      {status === 'pending' ? (
        <Loading />
      ) : (
        <>
          <div className='text-sm text-muted-foreground'>
            Total: <b>{filtered.length}</b>
          </div>
          <DataTable<Depot, unknown>
            columns={columns}
            data={filtered}
            rowClickable
            onRowClick={(row) => navigate(`/depots/${row.id}`)}
          />
          <div className='text-xs text-muted-foreground'>
            {isFetching ? 'Actualisation…' : null}
          </div>
        </>
      )}

      {/* Modale CRÉER/ÉDITER */}
      <AppDialog
        open={open}
        onOpenChange={setOpen}
        title={editing ? 'Modifier un dépôt' : 'Ajouter un dépôt'}
        asForm
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
        footer={
          <ModalActions
            onConfirm={submit}
            confirmLabel='Valider'
            confirmLoading={createMut.isPending || updateMut.isPending}
          />
        }
      >
        <div className='w-full px-4 py-2 space-y-3'>
          <Field label='Nom'>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder='Ex: Entrepôt Plateau'
            />
          </Field>

          <Field label='Adresse (optionnel)'>
            <Input
              value={form.address}
              onChange={(e) =>
                setForm((f) => ({ ...f, address: e.target.value }))
              }
              placeholder='Adresse / repère'
            />
          </Field>

          <Field label='Par défaut ?'>
            <Select
              value={form.isDefault}
              onValueChange={(v: 'YES' | 'NO') =>
                setForm((f) => ({ ...f, isDefault: v }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='NO'>Non</SelectItem>
                <SelectItem value='YES'>Oui</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
      </AppDialog>

      {/* Modale SUPPRIMER */}
      <AppDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title='Supprimer le dépôt'
        description='Cette action est irréversible.'
        footer={
          <ModalActions
            confirmVariant='destructive'
            confirmLabel='Supprimer'
            confirmLoading={deleteMut.isPending}
            onConfirm={confirmDelete}
          />
        }
      >
        <div className='px-4 py-2 text-sm'>
          Voulez-vous vraiment supprimer ce dépôt ?
        </div>
      </AppDialog>
    </div>
  )
}

function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className='grid gap-1.5'>
      <Label>{label}</Label>
      {children}
    </div>
  )
}
